import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { APP_PATHS } from "../../../utils/appPaths";
import { useQuery } from "@tanstack/react-query";
import DataTable from "react-data-table-component";
import { Breadcrumbs, Btn } from "../../../AbstractElements";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Badge,
} from "reactstrap";
import ContributorService from "../../../Services/contributors/ContributorService";
import ProductService from "../../../Services/products/ProductService";
import SpinnerLoader from "../../Loaders/Spinner";

const ContributorDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const contributorId = Number(id);

  const {
    data: contributorsResponse,
    isLoading: isContributorsLoading,
    isError: isContributorsError,
    error: contributorsError,
  } = useQuery({
    queryKey: ["contributors"],
    queryFn: ContributorService.getAllContributors,
  });

  const {
    data: productsResponse,
    isLoading: isProductsLoading,
    isError: isProductsError,
    error: productsError,
  } = useQuery({
    queryKey: ["products"],
    queryFn: ProductService.getAllProducts,
  });

  const contributor = useMemo(() => {
    const contributors = contributorsResponse?.data || [];
    return contributors.find((c) => Number(c.id) === contributorId);
  }, [contributorsResponse, contributorId]);

  const linkedRows = useMemo(() => {
    const rawProducts = productsResponse?.data;
    const products = Array.isArray(rawProducts)
      ? rawProducts
      : Array.isArray(rawProducts?.data)
      ? rawProducts.data
      : [];

    return products.flatMap((product) => {
      const assets = Array.isArray(product?.assets) ? product.assets : [];

      return assets.flatMap((asset) => {
        const contributors = Array.isArray(asset?.contributors)
          ? asset.contributors
          : [];

        const matchedContributor = contributors.find(
          (c) => Number(c.id) === contributorId
        );

        if (!matchedContributor) {
          return [];
        }

        const royalties = Array.isArray(matchedContributor.royalties)
          ? matchedContributor.royalties
          : [];
        const split = royalties
          .map((r) =>
            r?.title && r?.percentage != null
              ? `${r.title}: ${r.percentage}%`
              : r?.percentage != null
              ? `${r.percentage}%`
              : null
          )
          .filter(Boolean)
          .join(" | ");

        return [
          {
            product_name: product?.product_name || product?.name || "N/A",
            asset_name: asset?.asset_name || "N/A",
            isrc: asset?.ISRC || "N/A",
            release_date: asset?.release_date || "N/A",
            split: split || "N/A",
          },
        ];
      });
    });
  }, [productsResponse, contributorId]);

  const roles = Array.isArray(contributor?.contributor_role)
    ? contributor.contributor_role
    : contributor?.contributor_role
    ? [contributor.contributor_role]
    : [];

  const linkedColumns = [
    {
      name: "Product",
      selector: (row) => row.product_name,
      sortable: true,
    },
    {
      name: "Asset",
      selector: (row) => row.asset_name,
      sortable: true,
    },
    {
      name: "ISRC",
      selector: (row) => row.isrc,
      sortable: true,
    },
    {
      name: "Release Date",
      selector: (row) => row.release_date,
      sortable: true,
    },
    {
      name: "Split",
      selector: (row) => row.split,
      grow: 2,
    },
  ];

  const handleNavBack = () => {
    navigate(APP_PATHS.contributors);
  };

  if (isContributorsLoading || isProductsLoading) {
    return <SpinnerLoader />;
  }

  if (isContributorsError || isProductsError) {
    return (
      <div className="alert alert-danger">
        Error loading contributor details:{" "}
        {contributorsError?.message || productsError?.message}
      </div>
    );
  }

  if (!contributor) {
    return <div className="alert alert-warning">Contributor not found.</div>;
  }

  return (
    <>
      <Breadcrumbs
        parent="Contributors"
        title="Contributor Details"
        mainTitle={contributor.contributor_name || "Contributor"}
        buttons={[
          <Btn
            attrBtn={{
              size: "sm",
              color: "primary",
              onClick: handleNavBack,
            }}
          >
            <i className="fa fa-arrow-left"></i> Go Back
          </Btn>,
        ]}
      />

      <Container fluid={true}>
        <Row>
          <Col md="12" lg="5">
            <Card>
              <CardHeader>
                <h5>Bank Information</h5>
              </CardHeader>
              <CardBody>
                <p>
                  <strong>Name:</strong> {contributor.contributor_name || "N/A"}
                </p>
                <p>
                  <strong>Role:</strong>{" "}
                  {roles.length > 0 ? (
                    roles.map((role, idx) => (
                      <Badge key={`${role}-${idx}`} color="primary" className="me-1">
                        {role}
                      </Badge>
                    ))
                  ) : (
                    "N/A"
                  )}
                </p>
                <p>
                  <strong>Bank Account:</strong>{" "}
                  {contributor.bank_account_number || "N/A"}
                </p>
                <p>
                  <strong>IBAN:</strong> {contributor.bank_iban_number || "N/A"}
                </p>
                <p>
                  <strong>SWIFT:</strong> {contributor.bank_swift_number || "N/A"}
                </p>
                <p>
                  <strong>Bank Name:</strong> {contributor.bank_name || "N/A"}
                </p>
                <p>
                  <strong>Currency:</strong> {contributor.currency || "N/A"}
                </p>
                <p>
                  <strong>CA IPI:</strong> {contributor.ca_ipi || "N/A"}
                </p>
              </CardBody>
            </Card>
          </Col>

          <Col md="12" lg="7">
            <Card>
              <CardHeader>
                <h5>Linked Products / Assets / Splits</h5>
              </CardHeader>
              <CardBody>
                <div className="ctr-table-wrap">
                  <DataTable
                    columns={linkedColumns}
                    data={linkedRows}
                    striped
                    highlightOnHover
                    responsive
                    pagination
                    noDataComponent="No linked assets found for this contributor."
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ContributorDetails;
